import { queryOptions, type QueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/shared/api";
import type {
  SpaceSummary,
  SpaceDetail,
  SpaceInvite,
  SessionVisibility,
  AccessMode,
  SpaceRole,
} from "./types";

export interface InvitePreview {
  space: {
    id: string;
    name: string;
    description: string;
    session_visibility: SessionVisibility;
    access_mode: AccessMode;
  };
  role: SpaceRole;
}

export interface SpaceCreateBody {
  name: string;
  description?: string;
  session_visibility?: SessionVisibility;
  access_mode?: AccessMode;
}

export interface SpaceUpdateBody {
  name?: string;
  description?: string;
  session_visibility?: SessionVisibility;
  access_mode?: AccessMode;
}

export const spacesListQueryOptions = () =>
  queryOptions({
    queryKey: ["spaces"],
    queryFn: async (): Promise<SpaceSummary[]> => {
      const resp = await apiFetch("/api/spaces");
      if (!resp.ok) throw new Error("Не удалось загрузить пространства");
      const data: { spaces: SpaceSummary[] } = await resp.json();
      return data.spaces ?? [];
    },
    staleTime: 30_000,
  });

export const personalSpaceQueryOptions = () =>
  queryOptions({
    queryKey: ["spaces", "personal"],
    queryFn: async (): Promise<SpaceSummary> => {
      const resp = await apiFetch("/api/spaces/personal");
      if (!resp.ok) throw new Error("Не удалось получить личное пространство");
      return resp.json();
    },
    // Pretty stable — only changes when the user deletes their personal
    // space and we lazy-create another. Long staleTime keeps every
    // «Новая задача» click from going to the network.
    staleTime: 5 * 60_000,
  });

export const spaceDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ["spaces", id],
    queryFn: async (): Promise<SpaceDetail> => {
      const resp = await apiFetch(`/api/spaces/${id}`);
      if (!resp.ok) throw new Error("Не удалось загрузить пространство");
      return resp.json();
    },
    staleTime: 30_000,
  });

export const spaceInvitesQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ["spaces", id, "invites"],
    queryFn: async (): Promise<SpaceInvite[]> => {
      const resp = await apiFetch(`/api/spaces/${id}/invites`);
      if (!resp.ok) throw new Error("Не удалось загрузить приглашения");
      return resp.json();
    },
    staleTime: 30_000,
  });

export async function createSpace(body: SpaceCreateBody): Promise<SpaceSummary> {
  const resp = await apiFetch("/api/spaces", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!resp.ok) throw new Error((await resp.json()).detail ?? "Ошибка создания");
  return resp.json();
}

export async function updateSpace(id: string, body: SpaceUpdateBody): Promise<SpaceSummary> {
  const resp = await apiFetch(`/api/spaces/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!resp.ok) throw new Error((await resp.json()).detail ?? "Ошибка обновления");
  return resp.json();
}

export async function deleteSpace(id: string): Promise<void> {
  const resp = await apiFetch(`/api/spaces/${id}`, { method: "DELETE" });
  if (!resp.ok && resp.status !== 204) {
    throw new Error((await resp.json()).detail ?? "Ошибка удаления");
  }
}

export async function addMember(
  spaceId: string,
  body: { email?: string; user_id?: string; role: SpaceRole },
): Promise<{ user_id: string; email: string; name: string; role: SpaceRole }> {
  const resp = await apiFetch(`/api/spaces/${spaceId}/members`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!resp.ok) throw new Error((await resp.json()).detail ?? "Ошибка добавления");
  return resp.json();
}

export async function setMemberRole(
  spaceId: string,
  memberId: string,
  role: SpaceRole,
): Promise<void> {
  const resp = await apiFetch(`/api/spaces/${spaceId}/members/${memberId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ role }),
  });
  if (!resp.ok) throw new Error((await resp.json()).detail ?? "Ошибка изменения роли");
}

export async function removeMember(spaceId: string, memberId: string): Promise<void> {
  const resp = await apiFetch(`/api/spaces/${spaceId}/members/${memberId}`, {
    method: "DELETE",
  });
  if (!resp.ok && resp.status !== 204) {
    throw new Error((await resp.json()).detail ?? "Ошибка удаления участника");
  }
}

export async function transferOwnership(spaceId: string, userId: string): Promise<void> {
  const resp = await apiFetch(`/api/spaces/${spaceId}/transfer-ownership`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ user_id: userId }),
  });
  if (!resp.ok) throw new Error((await resp.json()).detail ?? "Ошибка передачи владения");
}

export async function createInvite(
  spaceId: string,
  body: { role?: SpaceRole; expires_in_hours?: number; max_uses?: number } = {},
): Promise<SpaceInvite> {
  const resp = await apiFetch(`/api/spaces/${spaceId}/invites`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!resp.ok) throw new Error((await resp.json()).detail ?? "Ошибка создания инвайта");
  return resp.json();
}

export async function revokeInvite(spaceId: string, token: string): Promise<void> {
  const resp = await apiFetch(`/api/spaces/${spaceId}/invites/${token}`, { method: "DELETE" });
  if (!resp.ok && resp.status !== 204) {
    throw new Error((await resp.json()).detail ?? "Ошибка отзыва");
  }
}

export const invitePreviewQueryOptions = (token: string) =>
  queryOptions({
    queryKey: ["invites", "preview", token],
    queryFn: async (): Promise<InvitePreview> => {
      const resp = await apiFetch(`/api/invites/${token}`);
      if (!resp.ok) {
        const detail = (await resp.json().catch(() => ({}))).detail;
        const err = new Error(detail ?? "Не удалось загрузить приглашение") as Error & { status?: number };
        err.status = resp.status;
        throw err;
      }
      return resp.json();
    },
    retry: false,
    staleTime: 0,
  });

export async function acceptInvite(token: string): Promise<{ space_id: string; role: SpaceRole }> {
  const resp = await apiFetch(`/api/invites/${token}`, { method: "POST" });
  if (!resp.ok) {
    const detail = (await resp.json().catch(() => ({}))).detail;
    const err = new Error(detail ?? "Ошибка приёма") as Error & { status?: number };
    err.status = resp.status;
    throw err;
  }
  return resp.json();
}

/** Wipe every space-scoped query so the active-space change is reflected
 *  immediately in chat, agents, connectors, routines, sessions, etc. The
 *  alternative — listing every key — would silently rot whenever a new
 *  scoped query gets added. */
export function invalidateSpaceScopedQueries(qc: QueryClient): void {
  qc.invalidateQueries({ queryKey: ["sessions"] });
  qc.invalidateQueries({ queryKey: ["agents"] });
  qc.invalidateQueries({ queryKey: ["connectors"] });
  qc.invalidateQueries({ queryKey: ["skills"] });
  qc.invalidateQueries({ queryKey: ["routines"] });
  qc.invalidateQueries({ queryKey: ["spaces"] });
}
